(ns ui.components.rich-text.tiptap
  (:require
   [clojure.string :as string]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.components.modal.view :as modal]
   [ui.components.select.select :as select]
   [ui.util :as uu]
   ["@tiptap/core" :refer [Editor]]
   ["@tiptap/starter-kit" :default StarterKit]
   ["@tiptap/extension-link" :default Link]
   ["@tiptap/extension-underline" :default Underline]
   ["@tiptap/extension-text-align" :default TextAlign]
   ["@tiptap/extension-placeholder" :default Placeholder]))

;; Helpers

(defn empty-editor-value?
  [v]
  (let [value (-> v str string/trim)]
    (or (empty? value)
        (= value "<p></p>")
        (= value "<p><br></p>")
        (= value "<p><br class=\"ProseMirror-trailingBreak\"></p>"))))

(defn normalize-legacy-html
  "Converts Draft.js HTML output to TipTap-compatible format"
  [html]
  (when html
    (-> html
        ;; Convert empty paragraph to a paragraph with non-breaking space
        ;; This preserves the empty line in TipTap
        (string/replace #"(?i)<p[^>]*>\s*<br\s*/?>\s*</p>" "<p></p>")
        (string/replace #"(?i)<p[^>]*>&nbsp;</p>" "<p></p>")

        ;; Underline: <ins> → <u>
        (string/replace #"(?i)<ins>" "<u>")
        (string/replace #"(?i)</ins>" "</u>")

        ;; Strikethrough: <del> → <s>
        (string/replace #"(?i)<del>" "<s>")
        (string/replace #"(?i)</del>" "</s>")

        ;; Inline bold style → <strong>
        (string/replace #"<span style=\"font-weight:\s*bold;?\">" "<strong>")

        ;; Inline italic style → <em>
        (string/replace #"<span style=\"font-style:\s*italic;?\">" "<em>")

        ;; Inline underline style → <u>
        (string/replace #"<span style=\"text-decoration:\s*underline;?\">" "<u>")

        ;; Remove empty style attributes
        (string/replace #"\s*style=\"\s*\"" "")

        ;; Remove standalone <br> between block elements (legacy paragraph spacers)
        (string/replace #"(?i)</p>\s*<br\s*/?>\s*<p" "</p><p")

        ;; Normalize line breaks
        (string/replace #"<br\s*/?>" "<br>")

        ;; Remove figure/figcaption (from pasted content)
        (string/replace #"<figure[^>]*>" "")
        (string/replace #"</figure>" "")
        (string/replace #"<figcaption[^>]*>.*?</figcaption>" ""))))

(defn sanitize-html
  "Full sanitization including legacy normalization"
  [html]
  (normalize-legacy-html html))

;; Extensions

(defn create-extensions
  [{:keys [link? placeholder]}]
  (let [extensions #js [(.configure StarterKit #js {:heading #js {:levels #js [1 2 3]}})
                        Underline
                        (.configure TextAlign #js {:types #js ["heading" "paragraph"]})]]
    (when link?
      (.push extensions (.configure Link #js {:openOnClick false
                                              :HTMLAttributes #js {:target "_blank"
                                                                   :class "text-blue-600 underline cursor-pointer"}})))
    (when placeholder
      (.push extensions (.configure Placeholder #js {:placeholder placeholder
                                                     :emptyEditorClass "before:content-[attr(data-placeholder)] before:text-gray-400 before:float-left before:h-0 before:pointer-events-none"})))
    extensions))

;; Toolbar Components

(defn toolbar-button
  [{:keys [on-click active? title disabled?]} & children]
  (into
   [:button
    {:type "button"
     :class (uu/cm "bg-transparent border-0 rounded-none shadow-none m-0 p-2 cursor-pointer hover:bg-gray-100 transition-colors h-full flex items-center justify-center box-border"
                   (when active? "bg-gray-100")
                   (when disabled? "opacity-50 cursor-not-allowed"))
     :disabled disabled?
     :on-mouse-down (fn [e]
                      (.preventDefault e))
     :on-click (when-not disabled? on-click)
     :title title}]
   children))

(defn inline-toolbar
  [editor editor-state opts]
  [:div
   {:class "rounded grid grid-flow-col auto-cols-fr divide-x divide-input-light-border-paler border border-solid border-input-light-border-paler m-0 h-10"}
   (when (contains? (set opts) :bold)
     [toolbar-button
      {:on-click #(-> editor (.chain) (.focus) (.toggleBold) (.run))
       :active? (:bold? editor-state)
       :title "Bold (Ctrl+B)"}
      [:span {:class "font-bold"} "B"]])
   (when (contains? (set opts) :italic)
     [toolbar-button
      {:on-click #(-> editor (.chain) (.focus) (.toggleItalic) (.run))
       :active? (:italic? editor-state)
       :title "Italic (Ctrl+I)"}
      [:span {:class "italic"} "I"]])
   (when (contains? (set opts) :underline)
     [toolbar-button
      {:on-click #(-> editor (.chain) (.focus) (.toggleUnderline) (.run))
       :active? (:underline? editor-state)
       :title "Underline (Ctrl+U)"}
      [:span {:class "underline"} "U"]])
   (when (contains? (set opts) :strikethrough)
     [toolbar-button
      {:on-click #(-> editor (.chain) (.focus) (.toggleStrike) (.run))
       :active? (:strike? editor-state)
       :title "Strikethrough"}
      [:span {:class "line-through"} "S"]])
   (when (contains? (set opts) :monospace)
     [toolbar-button
      {:on-click #(-> editor (.chain) (.focus) (.toggleCode) (.run))
       :active? (:code? editor-state)
       :title "Monospace"}
      [:span {:class "font-mono"} "<>"]])])

(defn list-toolbar
  [editor editor-state]
  [:div
   {:class "rounded grid grid-cols-2 divide-x divide-input-light-border-paler border border-solid border-input-light-border-paler m-0 h-10"}
   [toolbar-button
    {:on-click #(-> editor (.chain) (.focus) (.toggleBulletList) (.run))
     :active? (:bullet-list? editor-state)
     :title "Bullet List"}
    [:i {:class "fa fa-list-ul"}]]
   [toolbar-button
    {:on-click #(-> editor (.chain) (.focus) (.toggleOrderedList) (.run))
     :active? (:ordered-list? editor-state)
     :title "Numbered List"}
    [:i {:class "fa fa-list-ol"}]]])

(defn alignment-toolbar
  [editor editor-state]
  [:div
   {:class "rounded grid grid-cols-4 divide-x divide-input-light-border-paler border border-solid border-input-light-border-paler m-0 h-10"}
   [toolbar-button
    {:on-click #(-> editor (.chain) (.focus) (.setTextAlign "left") (.run))
     :active? (:text-align-left? editor-state)
     :title "Align Left"}
    [:i {:class "fa fa-align-left"}]]
   [toolbar-button
    {:on-click #(-> editor (.chain) (.focus) (.setTextAlign "center") (.run))
     :active? (:text-align-center? editor-state)
     :title "Align Center"}
    [:i {:class "fa fa-align-center"}]]
   [toolbar-button
    {:on-click #(-> editor (.chain) (.focus) (.setTextAlign "right") (.run))
     :active? (:text-align-right? editor-state)
     :title "Align Right"}
    [:i {:class "fa fa-align-right"}]]
   [toolbar-button
    {:on-click #(-> editor (.chain) (.focus) (.setTextAlign "justify") (.run))
     :active? (:text-align-justify? editor-state)
     :title "Justify"}
    [:i {:class "fa fa-align-justify"}]]])

(defn link-toolbar
  [_editor _editor-state]
  (let [modal-open? (r/atom false)
        url-atom (r/atom "https://")]
    (fn [editor editor-state]
      (let [active-link? (:link? editor-state)
            current-url (when editor (.-href (.getAttributes editor "link")))
            open-modal! (fn []
                          (reset! url-atom (or current-url "https://"))
                          (reset! modal-open? true))
            close-modal! #(reset! modal-open? false)
            save-link! (fn []
                         (let [url (-> @url-atom str string/trim)]
                           (when editor
                             (if (empty? url)
                               (-> editor (.chain) (.focus) (.unsetLink) (.run))
                               (-> editor
                                   (.chain)
                                   (.focus)
                                   (.extendMarkRange "link")
                                   (.setLink #js {:href url :target "_blank"})
                                   (.run))))
                           (close-modal!)))
            remove-link! (fn []
                           (when editor
                             (-> editor (.chain) (.focus) (.unsetLink) (.run)))
                           (close-modal!))]
        [:<>
         [:div
          {:class "rounded border border-solid border-input-light-border-paler m-0 h-10"}
          [toolbar-button
           {:on-click open-modal!
            :active? active-link?
            :title "Add/Remove Link"}
           [:i {:class "fa fa-link"}]]]
         [modal/view
          {:visible? @modal-open?
           :size "440px"
           :hide! close-modal!
           :header-class "text-base"}
          "Add link"
          [:div
           {:class "flex flex-col gap-2"}
           [:label {:class "text-sm text-base-light-text"} "URL"]
           [:input
            {:class "w-full border border-solid border-input-light-border-paler rounded px-3 py-2 bg-base-light text-base-light-text"
             :type "url"
             :autoFocus true
             :value @url-atom
             :placeholder "https://"
             :on-change #(reset! url-atom (.. % -target -value))}]]
          [modal/footer-left-and-right
           [(when active-link?
              [button/secondary-hollow
               {:on-click remove-link!}
               "Remove link"])]
           [[button/secondary-hollow
             {:on-click close-modal!}
             "Cancel"]
            [button/primary-solid
             {:on-click save-link!}
             "Add"]]]]]))))
;; Block Type Dropdown

(def block-type-labels
  {:normal "Normal"
   :h1 "Heading 1"
   :h2 "Heading 2"
   :h3 "Heading 3"})

(defn get-current-block-type
  [editor]
  (cond
    (and editor (.isActive editor "heading" #js {:level 1}))
    :h1

    (and editor (.isActive editor "heading" #js {:level 2}))
    :h2

    (and editor (.isActive editor "heading" #js {:level 3}))
    :h3

    :else
    :normal))

(defn set-editor-block-type!
  [editor block-type]
  (when editor
    (case block-type
      :normal (-> editor (.chain) (.focus) (.setParagraph) (.run))
      :h1 (-> editor (.chain) (.focus) (.setHeading #js {:level 1}) (.run))
      :h2 (-> editor (.chain) (.focus) (.setHeading #js {:level 2}) (.run))
      :h3 (-> editor (.chain) (.focus) (.setHeading #js {:level 3}) (.run))
      nil)))

(defn block-type-dropdown
  [_editor _editor-state _opts]
  (fn [editor editor-state {:keys [options] :or {options [:normal :h1 :h2]}}]
    (let [current-type (:block-type editor-state)
          available-options (filterv #(contains? (set options) %) [:normal :h1 :h2 :h3])
          select-options (mapv (fn [opt]
                                 {:value opt
                                  :label (get block-type-labels opt)})
                               available-options)]
      [select/view
       {:options select-options
        :value current-type
        :on-change (fn [block-type _opt]
                     (set-editor-block-type! editor block-type))
        :class "min-w-28 h-10"
        :open-menu-on-focus? true}])))

(defn get-editor-state
  "Extract current formatting state from editor"
  [editor]
  (when editor
    {:bold? (.isActive editor "bold")
     :italic? (.isActive editor "italic")
     :underline? (.isActive editor "underline")
     :strike? (.isActive editor "strike")
     :code? (.isActive editor "code")
     :bullet-list? (.isActive editor "bulletList")
     :ordered-list? (.isActive editor "orderedList")
     :link? (.isActive editor "link")
     :text-align-left? (.isActive editor #js {:textAlign "left"})
     :text-align-center? (.isActive editor #js {:textAlign "center"})
     :text-align-right? (.isActive editor #js {:textAlign "right"})
     :text-align-justify? (.isActive editor #js {:textAlign "justify"})
     :block-type (get-current-block-type editor)}))

;; Editor Toolbar

(defn editor-toolbar
  [_editor _opts]
  (fn [editor {:keys [link? show-alignment? show-block-type? toolbar-custom-buttons
                      inline-options block-type-options update-counter]
               :or {inline-options [:bold :italic :underline]
                    block-type-options [:normal :h1 :h2]}}]
    ;; Deref to trigger re-renders
    @update-counter
    (let [editor-state (get-editor-state editor)]
      [:div
       {:class "border-b-0 border-x border-t border-solid border-input-light-border-paler rounded-t gap-3 bg-base-light text-base-light-text p-2 flex flex-row items-center flex-wrap"}
       (when show-block-type?
         [block-type-dropdown editor editor-state block-type-options])
       [inline-toolbar editor editor-state inline-options]
       [list-toolbar editor editor-state]
       (when show-alignment?
         [alignment-toolbar editor editor-state])
       (when link?
         [link-toolbar editor editor-state])
       (when toolbar-custom-buttons
         toolbar-custom-buttons)])))

;; ProseMirror Content Styles

(def editor-content-classes
  "Tailwind classes for ProseMirror content styling"
  (string/join " "
               ["max-w-none"
                ;; Base text size
                "text-sm"
                ;; Focus
                "focus:outline-none"
                ;; Paragraph spacing
                "[&_p]:my-1.5" "[&_p:first-child]:mt-0" "[&_p:last-child]:mb-0"
                ;; List styling
                "[&_ul]:list-disc" "[&_ul]:pl-6" "[&_ul]:my-2"
                "[&_ol]:list-decimal" "[&_ol]:pl-6" "[&_ol]:my-2"
                "[&_li]:my-1"
                ;; Link styling
                "[&_a]:text-blue-600" "[&_a]:underline" "[&_a]:cursor-pointer"
                ;; Heading styles
                "[&_h1]:text-2xl" "[&_h1]:font-bold" "[&_h1]:my-4" "[&_h1]:leading-tight"
                "[&_h2]:text-xl" "[&_h2]:font-bold" "[&_h2]:my-3" "[&_h2]:leading-tight"
                "[&_h3]:text-lg" "[&_h3]:font-bold" "[&_h3]:my-2" "[&_h3]:leading-tight"
                ;; Code/monospace
                "[&_code]:bg-gray-100" "[&_code]:px-1" "[&_code]:py-0.5" "[&_code]:rounded" "[&_code]:font-mono" "[&_code]:text-sm"
                ;; Strikethrough
                "[&_s]:line-through"
                ;; Underline
                "[&_u]:underline"
                ;; Legacy tag support (Draft.js compatibility)
                "[&_ins]:underline"
                "[&_del]:line-through"
                "[&_strike]:line-through"]))

;; Editor Component

(defn editor
  [{:keys [_value _placeholder _class _editor-class _on-change _on-focus _on-blur
           _toolbar-custom-buttons _link? _show-alignment? _show-block-type?
           _inline-options _block-type-options]}]
  (let [editor-atom (r/atom nil)
        container-ref (atom nil)
        update-counter (r/atom 0)
        handlers-atom (atom nil)]
    (r/create-class
     {:display-name "tiptap-editor"

      :component-did-mount
      (fn [this]
        (let [props (r/props this)
              {:keys [placeholder link? value]} props
              _ (reset! handlers-atom props)
              normalized-content (sanitize-html value)
              tiptap-editor
              (Editor.
               #js {:element @container-ref
                    :extensions (create-extensions {:link? link?
                                                    :placeholder placeholder})
                    :content (or normalized-content "")
                    :editorProps #js {:attributes #js {:class editor-content-classes}}
                    :onUpdate (fn [^js params]
                                (swap! update-counter inc)
                                (when-let [on-change (:on-change @handlers-atom)]
                                  (on-change (.getHTML (.-editor params)))))
                    :onTransaction (fn [_]
                                     (swap! update-counter inc))
                    :onFocus (fn [^js params]
                               (when-let [on-focus (:on-focus @handlers-atom)]
                                 (on-focus (.-event params))))
                    :onBlur (fn [^js params]
                              (when-let [on-blur (:on-blur @handlers-atom)]
                                (on-blur (.-event params))))
                    :onSelectionUpdate (fn [_]
                                         (swap! update-counter inc))})]
          (reset! editor-atom tiptap-editor)))

      :component-did-update
      (fn [this _old-argv]
        (reset! handlers-atom (r/props this)))

      :component-will-unmount
      (fn [_]
        (when-let [tiptap-editor @editor-atom]
          (.destroy tiptap-editor)))

      :reagent-render
      (fn [{:keys [class editor-class toolbar-custom-buttons link? show-alignment?
                   show-block-type? inline-options block-type-options]
            :or {show-alignment? true
                 inline-options [:bold :italic :underline]
                 block-type-options [:normal :h1 :h2]}}]
        @update-counter
        [:div
         {:class (uu/cm "p-0 border-0 rounded" class)}
         [editor-toolbar @editor-atom {:link? link?
                                       :show-alignment? show-alignment?
                                       :show-block-type? show-block-type?
                                       :inline-options inline-options
                                       :block-type-options block-type-options
                                       :toolbar-custom-buttons toolbar-custom-buttons
                                       :update-counter update-counter}]
         [:div
          {:class (uu/cm "border rounded-b rounded-t-none border-solid border-input-light-border-paler min-h-32 p-3"
                         "hover:border-input-light-border"
                         "focus-within:border-input-light-border"
                         "focus-within:ring-input-light-border"
                         "bg-input-light"
                         "text-input-light-text"
                         "outline-none"
                         "[&_.ProseMirror]:outline-none"
                         "[&_.ProseMirror]:min-h-24"
                         editor-class)
           :ref #(reset! container-ref %)}]])})))

;; Viewer Component (Read-only)

(defn viewer
  [_props]
  (let [editor-atom (r/atom nil)
        container-ref (atom nil)]
    (r/create-class
     {:display-name "tiptap-viewer"

      :component-did-mount
      (fn [this]
        (let [{:keys [value]} (r/props this)
              normalized-content (sanitize-html value)
              tiptap-editor
              (Editor.
               #js {:element @container-ref
                    :extensions (create-extensions {})
                    :content (or normalized-content "")
                    :editable false
                    :editorProps #js {:attributes #js {:class editor-content-classes}}})]
          (reset! editor-atom tiptap-editor)))

      :component-did-update
      (fn [this old-argv]
        (let [{:keys [value]} (r/props this)
              old-value (:value (second old-argv))]
          (when (and @editor-atom (not= value old-value))
            (let [normalized-content (sanitize-html value)]
              (.. @editor-atom -commands (setContent (or normalized-content "")))))))

      :component-will-unmount
      (fn [_]
        (when-let [tiptap-editor @editor-atom]
          (.destroy tiptap-editor)))

      :reagent-render
      (fn [{:keys [styles class]}]
        [:div
         {:class (uu/cm "[&_.ProseMirror]:outline-none" class)
          :style styles
          :ref #(reset! container-ref %)}])})))

;; Preset Toolbar Configurations (matching previous Draft.js options)

(def all-toolbar-options
  "Full toolbar with all options"
  {:show-alignment? true
   :show-block-type? true
   :link? true
   :inline-options [:bold :italic :underline :strikethrough :monospace]
   :block-type-options [:normal :h1 :h2 :h3]})

(def no-align-toolbar-options
  "Toolbar without alignment, with block types"
  {:show-alignment? false
   :show-block-type? true
   :link? true
   :inline-options [:bold :italic :underline :strikethrough :monospace]
   :block-type-options [:normal :h1 :h2]})

(def minimal-toolbar-options
  "Minimal toolbar - just basic formatting"
  {:show-alignment? false
   :show-block-type? false
   :link? false
   :inline-options [:bold :italic :underline]})
